/**
 * App Version Bulk Actions Component
 *
 * Action bar for performing bulk operations on selected app versions.
 * Supports activating and deactivating multiple versions at once.
 */

import { useState } from 'react';
import { CheckCircle2, XCircle, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useBulkUpdateAppVersions } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';

interface AppVersionBulkActionsProps {
  selectedCount: number;
  selectedIds: string[];
  onClearSelection: () => void;
  onSuccess: () => void;
}

/**
 * App Version Bulk Actions
 * Renders action bar with activate/deactivate bulk operations
 */
export function AppVersionBulkActions({
  selectedCount,
  selectedIds,
  onClearSelection,
  onSuccess,
}: AppVersionBulkActionsProps) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const bulkUpdateMutation = useBulkUpdateAppVersions();

  /**
   * Handle Bulk Activate
   * Activates all selected versions
   */
  const handleBulkActivate = async () => {
    setIsProcessing(true);
    try {
      const result = await bulkUpdateMutation.mutateAsync({
        ids: selectedIds,
        isActive: true,
      });

      toast({
        title: 'Versions Activated',
        description: `Successfully activated ${result.updated} version(s)`,
      });

      onSuccess();
    } catch (error) {
      toast({
        title: 'Activation Failed',
        description: error instanceof Error ? error.message : 'Failed to activate versions',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle Bulk Deactivate
   * Deactivates all selected versions
   */
  const handleBulkDeactivate = async () => {
    setIsProcessing(true);
    try {
      const result = await bulkUpdateMutation.mutateAsync({
        ids: selectedIds,
        isActive: false,
      });

      toast({
        title: 'Versions Deactivated',
        description: `Successfully deactivated ${result.updated} version(s)`,
      });

      onSuccess();
    } catch (error) {
      toast({
        title: 'Deactivation Failed',
        description: error instanceof Error ? error.message : 'Failed to deactivate versions',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="p-4 bg-accent">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">
            {selectedCount} version{selectedCount !== 1 ? 's' : ''} selected
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={handleBulkActivate}
            disabled={isProcessing || selectedIds.length === 0}
            className="gap-2"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Activate All
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={handleBulkDeactivate}
            disabled={isProcessing || selectedIds.length === 0}
            className="gap-2"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            Deactivate All
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            disabled={isProcessing}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </Card>
  );
}
